import React, { useState } from 'react'
import { SignOut, ArrowsClockwise, Trash, DownloadSimple } from '@phosphor-icons/react'
import { useInstallPrompt } from '../hooks/useInstallPrompt'

export default function ProfileSettingsModal({ isOpen, onClose, onForceResync }) {
  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const { canInstall, isInstalled, isIOS, promptInstall } = useInstallPrompt()
  const [keyboardShortcut, setKeyboardShortcut] = useState(() => {
    const stored = localStorage.getItem('commandPaletteShortcut')
    if (!stored) {
      localStorage.setItem('commandPaletteShortcut', 'cmd-k')
      return 'cmd-k'
    }
    return stored
  })

  const isMac = () => {
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0
  }

  const handleShortcutChange = (e) => {
    const newShortcut = e.target.value
    setKeyboardShortcut(newShortcut)
    localStorage.setItem('commandPaletteShortcut', newShortcut)
    window.dispatchEvent(new Event('commandPaletteShortcutChange'))
  }

  const handleLogout = async () => {
    localStorage.clear()

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    try {
      await fetch('/sign_out', {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      })
    } catch (error) {
      console.error('Logout error:', error)
    }
    window.location.href = '/'
  }

  const handleForceResync = () => {
    onForceResync()
    onClose()
  }

  const handleInstall = async () => {
    const installed = await promptInstall()
    if (installed) {
      console.log('[PWA] App installed successfully')
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') return

    setIsDeleting(true)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    try {
      const response = await fetch('/api/user', {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      })

      if (response.ok) {
        localStorage.clear()
        window.location.href = '/'
      } else {
        alert('Failed to delete account')
      }
    } catch (error) {
      console.error('Delete account error:', error)
      alert('Failed to delete account')
    } finally {
      setIsDeleting(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="modal modal-open">
        <div className="modal-box">
          <h3 className="font-bold text-lg mb-4">Settings</h3>

          <div className="space-y-4">
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text">Keyboard Shortcut</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={keyboardShortcut}
                onChange={handleShortcutChange}
              >
                <option value="cmd-k">{isMac() ? '⌘K' : 'Ctrl+K'}</option>
                <option value="slash">/</option>
              </select>
            </div>

            <div className="divider">Account</div>

            {!isInstalled && canInstall && (
              <button
                className="btn btn-primary btn-block justify-start"
                onClick={handleInstall}
              >
                <DownloadSimple size={20} />
                Install App
              </button>
            )}

            {!isInstalled && isIOS && (
              <div className="alert alert-info text-sm">
                <div>
                  <p className="font-semibold">Install on iOS</p>
                  <p className="text-xs">Tap Share → Add to Home Screen</p>
                </div>
              </div>
            )}

            <button
              className="btn btn-outline btn-block justify-start"
              onClick={handleForceResync}
            >
              <ArrowsClockwise size={20} />
              Force Resync All Gists
            </button>

            <button
              className="btn btn-outline btn-block justify-start"
              onClick={handleLogout}
            >
              <SignOut size={20} />
              Logout
            </button>

            <div className="divider">Danger Zone</div>

            <div className="bg-error/10 p-4 rounded-lg space-y-3">
              <p className="text-sm text-error font-semibold">
                Delete Account
              </p>
              <p className="text-xs text-base-content/70">
                This action cannot be undone. Type DELETE to confirm.
              </p>
              <input
                type="text"
                placeholder="Type DELETE to confirm"
                className="input input-bordered input-sm w-full"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
              />
              <button
                className="btn btn-error btn-sm btn-block"
                onClick={handleDeleteAccount}
                disabled={deleteConfirmation !== 'DELETE' || isDeleting}
              >
                <Trash size={16} />
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>

          <div className="modal-action">
            <button className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
      <div className="modal-backdrop bg-black/50" onClick={onClose}></div>
    </>
  )
}
